import React from "react";
import { useNavigate } from "react-router-dom";
import { LegalLayout, Section } from "@/components/LegalLayout";
import { Button } from "@/components/ui/button";
import { Store, Camera } from "lucide-react";

function Steps({ steps }) {
  return (
    <ol className="mt-2 space-y-4">
      {steps.map((s, i) => (
        <li key={i} className="flex gap-3">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-100 font-display text-sm font-bold text-emerald-700">{i + 1}</span>
          <div>
            <p className="font-semibold text-zinc-900">{s.title}</p>
            <p className="text-sm text-zinc-600">{s.body}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

export default function HowItWorks() {
  const navigate = useNavigate();
  return (
    <LegalLayout
      metaTitle="How Nearfluence Works — for creators & businesses"
      metaDescription="Nearfluence gives businesses and creators a simple way to find each other and collaborate across the UK. Here's how it works for each side."
      title="How Nearfluence Works"
      subtitle="Nearfluence gives businesses and creators a simple way to find each other and collaborate."
    >
      <Section id="creators" title="For Creators">
        <Steps steps={[
          { title: "Create your profile", body: "Build your Nearfluence creator profile and showcase your social platforms, audience, interests, location and content." },
          { title: "Discover opportunities", body: "Browse collaboration opportunities from businesses looking for creators." },
          { title: "Apply", body: "Interested in an opportunity? Apply using your Nearfluence profile without repeatedly entering the same information." },
          { title: "Receive invitations", body: "Businesses can also discover your profile and invite you directly to relevant opportunities." },
          { title: "Collaborate", body: "If selected, communicate with the business and manage your collaboration through Nearfluence where supported." },
        ]} />
        <div className="mt-4"><Button data-testid="hiw-join-creator" onClick={() => navigate("/auth?mode=register&role=creator")} className="rounded-full bg-emerald-600 hover:bg-emerald-700"><Camera className="mr-2 h-4 w-4" /> Join as a Creator</Button></div>
      </Section>

      <Section id="businesses" title="For Businesses">
        <Steps steps={[
          { title: "Create your business profile", body: "Tell creators about your business and what you do." },
          { title: "Find creators your way", body: "Search and discover creators directly, publish an opportunity and receive applications, or do both." },
          { title: "Review creators", body: "View creator profiles and relevant information to help decide who may suit your campaign." },
          { title: "Connect", body: "Invite creators, review applications and communicate with potential collaborators." },
          { title: "Collaborate", body: "Agree the campaign requirements, deliverables and compensation directly with the creator." },
        ]} />
        <div className="mt-4"><Button data-testid="hiw-join-business" onClick={() => navigate("/auth?mode=register&role=business")} variant="outline" className="rounded-full border-2 border-zinc-900"><Store className="mr-2 h-4 w-4" /> Join as a Business</Button></div>
      </Section>
    </LegalLayout>
  );
}
